import type { MouseButton, RemoteCursorMessage } from '../shared/datachannel';
import type { PlatformKind } from '../shared/platform';

export interface PointerInputState {
  rightMode(): boolean;
  getRemotePlatform?(): PlatformKind;
}

export interface PointerInputBinding {
  release(): void;
  hide(): void;
  refresh(): void;
  setRemoteCursor(cursor: RemoteCursorMessage): void;
  setRemoteCursorMode(mode: 'captured' | 'overlay'): void;
  clearRemoteCursor(): void;
  dispose(): void;
}

export type Point = { x: number; y: number };
export type ContentRect = { left: number; top: number; width: number; height: number };

export type ActivePress = {
  button: MouseButton;
  startX: number;
  startY: number;
  moved: boolean;
};

export const MOUSE_MOVE_INTERVAL = 8;
export const DOUBLE_CLICK_DISTANCE_PX = 10;
export const WHEEL_FLUSH_DELAY_MS = 4;
export const WHEEL_LINE_SIZE = 40;
export const MAX_PENDING_WHEEL_DELTA = 8192;
export const GENERIC_CURSOR = {
  windows: { width: 22, height: 30, hotspotX: 1, hotspotY: 1 },
  macos: { width: 22, height: 30, hotspotX: 1, hotspotY: 1 },
  linux: { width: 21, height: 29, hotspotX: 1, hotspotY: 1 },
  unknown: { width: 22, height: 30, hotspotX: 1, hotspotY: 1 },
} as const;

export const GENERIC_CURSOR_PATH = 'M1 1v27.4l7.2-7.1 4.6 8.8 4.2-2.1-4.5-8.7H21z';
export const GENERIC_CURSOR_FILL = 'M3.2 5.1v18.1l5-4.9 4.4 8.6 1.4-.7-4.4-8.5h8.1z';
export const GENERIC_CURSOR_CSS = new Map<PlatformKind, string>();

export function genericCursorCss(platform: PlatformKind): string {
  const cached = GENERIC_CURSOR_CSS.get(platform);
  if (cached) return cached;
  const metrics = GENERIC_CURSOR[platform];
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${metrics.width}" height="${metrics.height}" viewBox="0 0 22 30"><path fill="#10161d" d="${GENERIC_CURSOR_PATH}"/><path fill="#fff" d="${GENERIC_CURSOR_FILL}"/></svg>`;
  const dataUrl = `data:image/svg+xml,${encodeURIComponent(svg)}`;
  const css = `url("${dataUrl}") ${metrics.hotspotX} ${metrics.hotspotY}, default`;
  GENERIC_CURSOR_CSS.set(platform, css);
  return css;
}

export function createOptimisticCursor(parent: HTMLElement): HTMLDivElement {
  const cursor = document.createElement('div');
  cursor.className = 'optimistic-cursor';
  cursor.dataset.platform = 'unknown';
  cursor.dataset.cursor = 'generic';
  cursor.setAttribute('aria-hidden', 'true');
  cursor.innerHTML = `
    <svg class="cursor-generic" viewBox="0 0 22 30" focusable="false" aria-hidden="true">
      <path class="cursor-outline" d="M1 1v27.4l7.2-7.1 4.6 8.8 4.2-2.1-4.5-8.7H21z"></path>
      <path class="cursor-fill" d="M3.2 5.1v18.1l5-4.9 4.4 8.6 1.4-.7-4.4-8.5h8.1z"></path>
    </svg>
    <img class="cursor-image" alt="" draggable="false">`;
  parent.appendChild(cursor);
  return cursor;
}

export function isPointerInput(event: PointerEvent): boolean {
  // 触摸继续由 touch.ts 处理，避免同一手势被发送两遍。
  return event.pointerType === 'mouse' || event.pointerType === 'pen';
}

export function mapButton(button: number): MouseButton | null {
  if (button === 0) return 'left';
  if (button === 1) return 'middle';
  if (button === 2) return 'right';
  return null;
}
